export const TERMINAL_AUTO_ACK_DELAY_MS = 60_000

export type AttemptBackgroundJobAutoAckArgs = {
  backgroundJobId: number
  autoAckAttemptedJobIds: Set<number>
  acknowledgeBackgroundJob: (backgroundJobId: number) => Promise<void>
}

/** Acknowledges a terminal background job once when auto-dismiss rules pass. */
export const attemptBackgroundJobAutoAck = async ({
  backgroundJobId,
  autoAckAttemptedJobIds,
  acknowledgeBackgroundJob,
}: AttemptBackgroundJobAutoAckArgs): Promise<void> => {
  if (autoAckAttemptedJobIds.has(backgroundJobId)) {
    return
  }

  autoAckAttemptedJobIds.add(backgroundJobId)

  try {
    await acknowledgeBackgroundJob(backgroundJobId)
  } catch (error) {
    console.error(error)
    autoAckAttemptedJobIds.delete(backgroundJobId)
  }
}

export type ScheduleBackgroundJobAutoAckArgs = AttemptBackgroundJobAutoAckArgs & {
  autoAckTimerByJobId: Map<number, ReturnType<typeof setTimeout>>
  delayMs?: number
}

/** Clears a pending auto-ack timer without marking the job as acknowledged. */
export const cancelScheduledBackgroundJobAutoAck = (
  backgroundJobId: number,
  autoAckTimerByJobId: Map<number, ReturnType<typeof setTimeout>>
) => {
  const timer = autoAckTimerByJobId.get(backgroundJobId)
  if (timer) {
    clearTimeout(timer)
    autoAckTimerByJobId.delete(backgroundJobId)
  }
}

/** Schedules a one-shot auto-ack after the terminal linger delay (prototype parity). */
export const scheduleBackgroundJobAutoAck = ({
  backgroundJobId,
  autoAckAttemptedJobIds,
  autoAckTimerByJobId,
  acknowledgeBackgroundJob,
  delayMs = TERMINAL_AUTO_ACK_DELAY_MS,
}: ScheduleBackgroundJobAutoAckArgs): void => {
  if (autoAckAttemptedJobIds.has(backgroundJobId) || autoAckTimerByJobId.has(backgroundJobId)) {
    return
  }

  const timer = setTimeout(() => {
    autoAckTimerByJobId.delete(backgroundJobId)
    void attemptBackgroundJobAutoAck({
      backgroundJobId,
      autoAckAttemptedJobIds,
      acknowledgeBackgroundJob,
    })
  }, delayMs)

  autoAckTimerByJobId.set(backgroundJobId, timer)
}

export const clearAllScheduledBackgroundJobAutoAcks = (
  autoAckTimerByJobId: Map<number, ReturnType<typeof setTimeout>>
) => {
  for (const timer of autoAckTimerByJobId.values()) {
    clearTimeout(timer)
  }
  autoAckTimerByJobId.clear()
}
